import Joi from "joi-browser";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScriptsv2";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { FormInput, FormRadioButton } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";

function SAFYURICA() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = {
    SelectedProgram: Joi.string().required(),
    Date: Joi.string().required().label("Date"),
    NoProblems: Joi.string().required(),
    Improvement: Joi.string().required(),
    Solving: Joi.string().required(),
    Worthwhile: Joi.string().required(),
    NotProblem: Joi.string().required(),
    Slip: Joi.string().required(),
    Finally: Joi.string().required(),
    Change: Joi.string().required(),
    KeepUp: Joi.string().required(),
    Difficult: Joi.string().required(),
    Waste: Joi.string().required(),
    Understand: Joi.string().required(),
    Faults: Joi.string().required(),
    WorkingHard: Joi.string().required(),
    HaveProblem: Joi.string().required(),
    NoFollowThrough: Joi.string().required(),
    AtLeast: Joi.string().required(),
    Resolved: Joi.string().required(),
    Ideas: Joi.string().required(),
    LikeHelp: Joi.string().required(),
    HelpHere: Joi.string().required(),
    Boost: Joi.string().required(),
    PartOfProblem: Joi.string().required(),
    Advice: Joi.string().required(),
    Actually: Joi.string().required(),
    Boring: Joi.string().required(),
    PreventRelapse: Joi.string().required(),
    Recurrence: Joi.string().required(),
    Worries: Joi.string().required(),
    Actively: Joi.string().required(),
    Cope: Joi.string().required(),
    Haunt: Joi.string().required(),
    Precontemplation: Joi.string().required(),
    Contemplation: Joi.string().required(),
    Action: Joi.string().required(),
    Maintenance: Joi.string().required(),
    Readiness: Joi.string().required(),
  };

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails?.Active, formsyDetails?.FinalSigned]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && data.SelectedProgram) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram.value = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, data.SelectedProgram]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.value) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  // Precontemplation
  useEffect(() => {
    if (!data.SelectedProgram?.value) {
      return;
    }

    let count = 0;
    let total = 0;

    if (data.NoProblems.value) {
      count++;
      total += parseInt(data.NoProblems.value);
    }

    if (data.NotProblem.value) {
      count++;
      total += parseInt(data.NotProblem.value);
    }

    if (data.Waste.value) {
      count++;
      total += parseInt(data.Waste.value);
    }

    if (data.Faults.value) {
      count++;
      total += parseInt(data.Faults.value);
    }

    if (data.PartOfProblem.value) {
      count++;
      total += parseInt(data.PartOfProblem.value);
    }

    if (data.Boring.value) {
      count++;
      total += parseInt(data.Boring.value);
    }

    if (data.Worries.value) {
      count++;
      total += parseInt(data.Worries.value);
    }

    let score = 0;

    if (count > 0) {
      score = total / count;
    }

    setData((d) => {
      const obj = { ...d };
      obj.Precontemplation.value = score.toFixed();
      return obj;
    });
  }, [
    data.SelectedProgram?.value,
    data.NoProblems?.value,
    data.NotProblem?.value,
    data.Waste?.value,
    data.Faults?.value,
    data.PartOfProblem?.value,
    data.Boring?.value,
    data.Worries?.value,
  ]);

  // Contemplation
  useEffect(() => {
    if (!data.SelectedProgram?.value) {
      return;
    }

    let count = 0;
    let total = 0;

    if (data.Improvement.value) {
      count++;
      total += parseInt(data.Improvement.value);
    }

    if (data.Change.value) {
      count++;
      total += parseInt(data.Change.value);
    }

    if (data.Understand.value) {
      count++;
      total += parseInt(data.Understand.value);
    }

    if (data.HaveProblem.value) {
      count++;
      total += parseInt(data.HaveProblem.value);
    }

    if (data.Ideas.value) {
      count++;
      total += parseInt(data.Ideas.value);
    }

    if (data.HelpHere.value) {
      count++;
      total += parseInt(data.HelpHere.value);
    }

    if (data.Advice.value) {
      count++;
      total += parseInt(data.Advice.value);
    }

    let score = 0;

    if (count > 0) {
      score = total / count;
    }

    setData((d) => {
      const obj = { ...d };
      obj.Contemplation.value = score.toFixed();
      return obj;
    });
  }, [
    data.SelectedProgram?.value,
    data.Improvement?.value,
    data.Change?.value,
    data.Understand?.value,
    data.HaveProblem?.value,
    data.Ideas?.value,
    data.HelpHere?.value,
    data.Advice?.value,
  ]);

  // Action
  useEffect(() => {
    if (!data.SelectedProgram?.value) {
      return;
    }

    let count = 0;
    let total = 0;

    if (data.Solving.value) {
      count++;
      total += parseInt(data.Solving.value);
    }

    if (data.Finally.value) {
      count++;
      total += parseInt(data.Finally.value);
    }

    if (data.Difficult.value) {
      count++;
      total += parseInt(data.Difficult.value);
    }

    if (data.WorkingHard.value) {
      count++;
      total += parseInt(data.WorkingHard.value);
    }

    if (data.AtLeast.value) {
      count++;
      total += parseInt(data.AtLeast.value);
    }

    if (data.Actually.value) {
      count++;
      total += parseInt(data.Actually.value);
    }

    if (data.Actively.value) {
      count++;
      total += parseInt(data.Actively.value);
    }

    let score = 0;

    if (count > 0) {
      score = total / count;
    }

    setData((d) => {
      const obj = { ...d };
      obj.Action.value = score.toFixed();
      return obj;
    });
  }, [
    data.SelectedProgram?.value,
    data.Solving?.value,
    data.Finally?.value,
    data.Difficult?.value,
    data.WorkingHard?.value,
    data.AtLeast?.value,
    data.Actually?.value,
    data.Actively?.value,
  ]);

  // Maintenance
  useEffect(() => {
    if (!data.SelectedProgram?.value) {
      return;
    }

    let count = 0;
    let total = 0;

    if (data.Slip.value) {
      count++;
      total += parseInt(data.Slip.value);
    }

    if (data.NoFollowThrough.value) {
      count++;
      total += parseInt(data.NoFollowThrough.value);
    }

    if (data.Resolved.value) {
      count++;
      total += parseInt(data.Resolved.value);
    }

    if (data.Boost.value) {
      count++;
      total += parseInt(data.Boost.value);
    }

    if (data.PreventRelapse.value) {
      count++;
      total += parseInt(data.PreventRelapse.value);
    }

    if (data.Recurrence.value) {
      count++;
      total += parseInt(data.Recurrence.value);
    }

    if (data.Haunt.value) {
      count++;
      total += parseInt(data.Haunt.value);
    }

    let score = 0;

    if (count > 0) {
      score = total / count;
    }

    setData((d) => {
      const obj = { ...d };
      obj.Maintenance.value = score.toFixed();
      return obj;
    });
  }, [
    data.SelectedProgram?.value,
    data.Slip?.value,
    data.NoFollowThrough?.value,
    data.Resolved?.value,
    data.Boost?.value,
    data.PreventRelapse?.value,
    data.Recurrence?.value,
    data.Haunt?.value,
  ]);

  useEffect(() => {
    if (!data.Precontemplation?.value) {
      return;
    }

    const contemplation = parseFloat(data.Contemplation.value);
    const action = parseFloat(data.Action.value);
    const maintenance = parseFloat(data.Maintenance.value);
    const precontemplation = parseFloat(data.Precontemplation.value);
    const readiness = contemplation + action + maintenance - precontemplation;

    setData((d) => {
      const obj = { ...d };
      obj.Readiness.value = readiness.toFixed();
      return obj;
    });
  }, [
    data.Precontemplation?.value,
    data.Contemplation?.value,
    data.Action?.value,
    data.Maintenance?.value,
  ]);

  const readinessStage = useMemo(() => {
    if (!data.Readiness?.value) {
      return "";
    }

    if (data.Readiness.value <= 8) {
      return "Precontemplation";
    }

    if (data.Readiness.value > 8 && data.Readiness.value <= 11) {
      return "Contemplation";
    }

    if (data.Readiness.value > 11 && data.Readiness.value <= 14) {
      return "Preparation (Action)";
    }

    if (data.Readiness.value > 14) {
      return "Maintenance";
    }
  }, [data.Readiness?.value]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="text-center mb-3">
                <h4>
                  University of Rhode Island Change Assessment Scale (URICA):
                  Psychotherapy Version
                </h4>
              </div>
              <div className="row mb-3">
                <div className="col">Client ID# {clientData.client?.mpi}</div>
                <div className="col d-flex align-items-center">
                  <div className="me-1">Date:</div>
                  <FormInput
                    name="Date"
                    error={errors.Date}
                    value={getFieldValue(data.Date)}
                    onChange={handleChange}
                    identical={compare("Date")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                    divClassName="h-100 d-flex align-items-center"
                    customMarginBottom="mb-0"
                    className="me-2"
                  />
                </div>
              </div>
              <b>
                <p style={{ textAlign: "justify", textJustify: "inter-word" }}>
                  EACH STATEMENT BELOW DESCRIBES HOW A PERSON MIGHT FEEL WHEN
                  STARTING THERAPY OR APPROACHING PROBLEMS IN THEIR LIVES.
                  PLEASE INDICATE THE EXTENT TO WHICH YOU TEND TO AGREE OR
                  DISAGREE WITH EACH STATEMENT. IN EACH CASE, MAKE YOUR CHOICE
                  IN TERMS OF HOW YOU FEEL RIGHT NOW, NOT WHAT YOU HAVE FELT IN
                  THE PAST OR WOULD LIKE TO FEEL. FOR ALL STATEMENTS THAT REFER
                  TO YOUR "PROBLEM", ANSWER IN TERMS OF PROBLEMS RELATED TO WHY
                  YOU ARE IN THERAPY. THE WORDS "HERE" AND "THIS PLACE" REFER TO
                  YOUR TREATMENT CENTER.
                </p>
                <p>
                  SELECT THE OPTION THAT BEST DESCRIBES HOW MUCH YOU AGREE OR
                  DISAGREE WITH EACH STATEMENT.
                </p>
              </b>
              <div className="row border-bottom border-dark text-center">
                <div className="col-5"></div>
                <div className="col">Strongly Disagree</div>
                <div className="col">Disagree</div>
                <div className="col">Undecided</div>
                <div className="col">Agree</div>
                <div className="col">Strongly Agree</div>
              </div>
              <div className="row my-2">
                <div className="col-5">
                  1. As far as I'm concerned, I don't have any problems that
                  need changing.
                  {errors.NoProblems ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.NoProblems}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="NoProblems"
                      value={data.NoProblems?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("NoProblems")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  2. I think I might be ready for some self-improvement.
                  {errors.Improvement ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Improvement}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Improvement"
                      value={data.Improvement?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Improvement")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  3. I am doing something about the problems that had been
                  bothering me.
                  {errors.Solving ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Solving}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Solving"
                      value={data.Solving?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Solving")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  4. It might be worthwhile to work on my problem.
                  {errors.Worthwhile ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Worthwhile}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Worthwhile"
                      value={data.Worthwhile?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Worthwhile")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  5. I'm not the problem one. It doesn't make much sense for me
                  to be here.
                  {errors.NotProblem ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.NotProblem}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="NotProblem"
                      value={data.NotProblem?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("NotProblem")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  6. It worries me that I might slip back on a problem I have
                  already changed, so I am here to seek help.
                  {errors.Slip ? (
                    <div className="mt-1 alert alert-danger">{errors.Slip}</div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Slip"
                      value={data.Slip?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Slip")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  7. I am finally doing some work on my problems.
                  {errors.Finally ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Finally}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Finally"
                      value={data.Finally?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Finally")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  8. I've been thinking that I might want to change something
                  about myself.
                  {errors.Change ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Change}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Change"
                      value={data.Change?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Change")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  9. I have been successful in working on my problem but I'm not
                  sure I can keep up the effort on my own.
                  {errors.KeepUp ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.KeepUp}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="KeepUp"
                      value={data.KeepUp?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("KeepUp")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  10. At times my problem is difficult, but I'm working on it.
                  {errors.Difficult ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Difficult}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Difficult"
                      value={data.Difficult?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Difficult")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  11. Trying to change is pretty much a waste of time for me
                  because the problem doesn't have to do with me.
                  {errors.Waste ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Waste}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Waste"
                      value={data.Waste?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Waste")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  12. I'm hoping this place will help me to better understand
                  myself.
                  {errors.Understand ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Understand}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Understand"
                      value={data.Understand?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Understand")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  13. I guess I have faults, but there's nothing that I really
                  need to change.
                  {errors.Faults ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Faults}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Faults"
                      value={data.Faults?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Faults")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  14. I am really working hard to change.
                  {errors.WorkingHard ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.WorkingHard}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="WorkingHard"
                      value={data.WorkingHard?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("WorkingHard")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  15. I have a problem and I really think I should work on it.
                  {errors.HaveProblem ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.HaveProblem}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="HaveProblem"
                      value={data.HaveProblem?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("HaveProblem")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  16. I'm not following though with what I had already changed
                  as well as I had hoped, and I'm here to prevent a relapse of
                  the problem.
                  {errors.NoFollowThrough ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.NoFollowThrough}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="NoFollowThrough"
                      value={data.NoFollowThrough?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("NoFollowThrough")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  17. Even though I'm not always successful in changing, I am at
                  least working on my problem.
                  {errors.AtLeast ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.AtLeast}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="AtLeast"
                      value={data.AtLeast?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("AtLeast")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  18. I thought once I had resolved the problem I would be free
                  of it, but sometimes I still find myself struggling with it.
                  {errors.Resolved ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Resolved}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Resolved"
                      value={data.Resolved?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Resolved")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  19. I wish I had more ideas on how to solve my problem.
                  {errors.Ideas ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Ideas}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Ideas"
                      value={data.Ideas?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Ideas")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  20. I have started working on my problems but I would like
                  help.
                  {errors.LikeHelp ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.LikeHelp}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="LikeHelp"
                      value={data.LikeHelp?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("LikeHelp")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  21. Maybe this place will be able to help me.
                  {errors.HelpHere ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.HelpHere}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="HelpHere"
                      value={data.HelpHere?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("HelpHere")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  22. I may need a boost right now to help me maintain the
                  changes I've already made.
                  {errors.Boost ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Boost}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Boost"
                      value={data.Boost?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Boost")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  23. I may be part of the problem, but I don't really think I
                  am.
                  {errors.PartOfProblem ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.PartOfProblem}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="PartOfProblem"
                      value={data.PartOfProblem?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("PartOfProblem")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  24. I hope that someone here will have some good advice for
                  me.
                  {errors.Advice ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Advice}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Advice"
                      value={data.Advice?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Advice")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  25. Anyone can talk about changing; I'm actually doing
                  something about it.
                  {errors.Actually ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Actually}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Actually"
                      value={data.Actually?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Actually")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  26. All this talk about psychology is boring. Why can't people
                  just forget about their problems?
                  {errors.Boring ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Boring}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Boring"
                      value={data.Boring?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Boring")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  27. I'm here to prevent myselffrom having a relapse of my
                  problem.
                  {errors.PreventRelapse ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.PreventRelapse}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="PreventRelapse"
                      value={data.PreventRelapse?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("PreventRelapse")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  28. It is frustrating, but I feel I might be having a
                  recurrence of a problem I thought I had resolved.
                  {errors.Recurrence ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Recurrence}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Recurrence"
                      value={data.Recurrence?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Recurrence")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  29. I have worries but so does the next person. Why spend time
                  thinking about them?
                  {errors.Worries ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Worries}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Worries"
                      value={data.Worries?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Worries")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  30. I am actively working on my problem.
                  {errors.Actively ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Actively}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Actively"
                      value={data.Actively?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Actively")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  31. I would rather cope with my faults than try to change
                  them.
                  {errors.Cope ? (
                    <div className="mt-1 alert alert-danger">{errors.Cope}</div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Cope"
                      value={data.Cope?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Cope")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <div className="row mb-2">
                <div className="col-5">
                  32. After all I had done to try and change my problem, every
                  now and then it comes back to haunt me.
                  {errors.Haunt ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Haunt}
                    </div>
                  ) : null}
                </div>
                {["1", "2", "3", "4", "5"].map((item) => (
                  <div className="col text-center" key={item}>
                    <FormRadioButton
                      name="Haunt"
                      value={data.Haunt?.value}
                      options={[{ _id: item, name: "" }]}
                      onChange={handleChange}
                      identical={compare("Haunt")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ))}
              </div>
              <hr />
              <div className="text-center mt-2">
                <h5>URICA Scoring</h5>
              </div>
              <div className="container border-top border-start text-center mb-4">
                <div className="row border-bottom">
                  <div className="col border-end py-2 fw-bold">
                    Precontemplation (PC)
                  </div>
                  <div className="col border-end py-2">
                    {getFieldValue(data.Precontemplation)}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2 fw-bold">
                    Contemplation (C)
                  </div>
                  <div className="col border-end py-2">
                    {getFieldValue(data.Contemplation)}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2 fw-bold">Action (A)</div>
                  <div className="col border-end py-2">
                    {getFieldValue(data.Action)}
                  </div>
                </div>
                <div className="row border-bottom border-2">
                  <div className="col border-end py-2 fw-bold">
                    Maintenance (M)
                  </div>
                  <div className="col border-end py-2">
                    {getFieldValue(data.Maintenance)}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2 fw-bold">
                    Readiness to Change (C + A + M - PC)
                  </div>
                  <div className="col border-end py-2 fw-bold">
                    {getFieldValue(data.Readiness)} - {readinessStage}
                  </div>
                </div>
              </div>
              <div className="text-center mt-2">
                <h5>Readiness Score</h5>
              </div>
              <div className="container mt-2 border-top border-start text-center">
                <div className="row border-bottom">
                  <div className="col border-end py-2 fw-bold">Stage</div>
                  <div className="col border-end py-2 fw-bold">
                    Group Average
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">Precontemplation</div>
                  <div className="col border-end py-2">8 or lower</div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">Contemplation</div>
                  <div className="col border-end py-2">8 - 11</div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    Preparation (Action)
                  </div>
                  <div className="col border-end py-2">11 - 14</div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">Maintenance</div>
                  <div className="col border-end py-2">14 and above</div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SAFYURICA;
